import { convertSubscription } from "../utils";
import {
  PaymentDetails,
  PCResponse,
  PCSubscription,
  Subscription,
} from "../../../contracts/nexar-api/types";
import { NexarApiContract } from "../../../contracts/nexar-api/nexar-api";
import NexarApi from "../actual-api/api";
import FixtureAPIGetUserSubscriptions from "./fixtures/get-user-subscriptions";
import FixtureAPIGetUserSubscriptionsEmpty from "./fixtures/get-user-subscriptions-empty";

import { setValue } from "../actual-api/storage";
import {
  ACCESS_TOKEN_COOKIE,
  NEXAR_PC_USER_ID,
  NEXAR_USER_ID,
  PC_ACCESS_TOKEN_COOKIE,
  PC_REFRESH_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
} from "../actual-api/consts";

// Import dev config for non-production environments (development/staging)
// Check REACT_APP_ENV to determine if we should load dev config
// This is baked into the bundle at build time, ensuring production builds exclude dev code
let devConfig: any = null;
if (process.env.REACT_APP_ENV !== 'production') {
  devConfig = require('../../../dev/dev-config').devConfig;
}

// Helper to get config values (falls back to safe defaults if devConfig not available)
const getConfig = () => {
  if (devConfig) {
    return devConfig.getConfig().mockApi;
  }
  // Fallback defaults for when devConfig is not available
  return {
    mockLogin: true,
    returnEmpty: false,
    isAuthenticated: true,
    failCancellation: false,
    failUpdate: false,
    failActivation: false,
    failSetupIntentCreation: false,
  };
};

const getSubscriptionsWithModules = async (): Promise<{
  data: Subscription[] | null;
}> => {
  const config = getConfig();
  let data = FixtureAPIGetUserSubscriptions;
  if (config.returnEmpty) {
    data = FixtureAPIGetUserSubscriptionsEmpty;
  }

  const subscriptions = data.payload.subscriptions as PCSubscription[];
  const convertedSubscriptions = subscriptions?.map((sub: PCSubscription) =>
    convertSubscription(sub)
  );
  return { data: convertedSubscriptions || null };
};

const getSubscriptionById = async ({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<{ data: Subscription }> => {
  // Import the lookup dynamically to avoid circular dependencies
  const { getSubscriptionFixtureById, AVAILABLE_IDS } = require('./subscription-lookup');
  
  const fixture = getSubscriptionFixtureById(subscriptionId);
  
  if (!fixture) {
    console.warn(`⚠️ Subscription ID "${subscriptionId}" not found in mock API. Available IDs:`, AVAILABLE_IDS);
    // Fallback to first available subscription or throw error
    throw new Error(`Subscription not found: ${subscriptionId}`);
  }

  const convertedSubscription = convertSubscription(
    fixture.payload?.subscription as PCSubscription
  );
  return { data: convertedSubscription };
};

async function validateOtpCodeAndLogin(
  countryCode: string,
  phoneNumber: string,
  digits: string,
  clientId: string
): Promise<void> {
  const config = getConfig();
  
  if (config.mockLogin) {
    // Mock successful login - generate and set dummy tokens in storage
    // These tokens have far-future expiration (9999999999) so they won't expire during development
    const mockBearerAccessToken = "eyJhbGciOiJFUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjk5OTk5OTk5OTksImlhdCI6MTcwNjc4MzQ4MCwibmJmIjoxNzA2NzgzNDgwLCJyeGRVc2VySWQiOiJtb2NrLXJ4ZGFlbW9uLXVzZXItaWQiLCJ0b2tlblR5cGUiOiJVU0VSIiwidXNlcklkIjoibW9jay11c2VyLWlkIn0.MOCK_BEARER_ACCESS_TOKEN";
    const mockBearerRefreshToken = "eyJhbGciOiJFUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjk5OTk5OTk5OTksImlhdCI6MTcwNjc4MzQ4MCwibmJmIjoxNzA2NzgzNDgwLCJ0b2tlblR5cGUiOiJSRUZSRVNIX1VTRVIiLCJ1c2VySWQiOiJtb2NrLXVzZXItaWQifQ.MOCK_BEARER_REFRESH_TOKEN";
    const mockPCAccessToken = "eyJhbGciOiJFUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjk5OTk5OTk5OTksImlhdCI6MTcwNjc4MzQ4MCwibmJmIjoxNzA2NzgzNDgwLCJ0b2tlblR5cGUiOiJQQ19BQ0NFU1MiLCJ1c2VySWQiOiJtb2NrLXBjLXVzZXItaWQifQ.MOCK_PC_ACCESS_TOKEN";
    const mockPCRefreshToken = "eyJhbGciOiJFUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjk5OTk5OTk5OTksImlhdCI6MTcwNjc4MzQ4MCwibmJmIjoxNzA2NzgzNDgwLCJ0b2tlblR5cGUiOiJQQ19SRUZSRVNIIiwidXNlcklkIjoibW9jay1wYy11c2VyLWlkIn0.MOCK_PC_REFRESH_TOKEN";
    
    setValue(ACCESS_TOKEN_COOKIE, mockBearerAccessToken);
    setValue(REFRESH_TOKEN_COOKIE, mockBearerRefreshToken);
    setValue(NEXAR_USER_ID, "mock-rxdaemon-user-id");
    setValue(PC_ACCESS_TOKEN_COOKIE, mockPCAccessToken);
    setValue(PC_REFRESH_TOKEN_COOKIE, mockPCRefreshToken);
    setValue(NEXAR_PC_USER_ID, "mock-pc-user-id");
    
    return Promise.resolve();
  } else {
    // If mock login is disabled, throw error to simulate failed login
    throw new Error("Mock login is disabled. Enable it in dev tools.");
  }
}

async function sendOtpCode(
  countryCode: string,
  phoneNumber: string
): Promise<{ clientId: string }> {
  const clientId = new Date().getTime().toString();
  return { clientId: clientId };
}

async function isAuthenticated(): Promise<boolean> {
  const config = getConfig();
  return Promise.resolve(config.isAuthenticated);
}

async function createSubscriptionSetupIntent({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<{ clientSecret: string }> {
  const config = getConfig();
  if (config.failSetupIntentCreation) {
    throw new Error("Failed to create setup intent");
  }
  return { clientSecret: `seti_mock_${subscriptionId}_secret_mock` };
}
const deleteSubscriptionById = async ({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<PCResponse> => {
  const config = getConfig();
  if (config.failCancellation) {
    return { error: {}, payload: {} };
  } else {
    return { success: {}, payload: {} };
  }
};

const updateSubscriptionById = async ({
  subscriptionId,
  paymentDetails,
}: {
  subscriptionId: string;
  paymentDetails: PaymentDetails;
}): Promise<PCResponse> => {
  const config = getConfig();
  if (config.failUpdate) {
    return { error: {}, payload: {} };
  } else {
    return { success: {}, payload: {} };
  }
};

const activateSubscriptionById = async ({
  subscriptionId,
  paymentDetails,
}: {
  subscriptionId: string;
  paymentDetails: PaymentDetails;
}): Promise<PCResponse> => {
  const config = getConfig();
  if (config.failActivation) {
    return { error: {}, payload: {} };
  } else {
    return { success: {}, payload: {} };
  }
};

const getUserData = async () => {
  return {};
};
const getPersonalCloudUserId = () => {
  return "5e0b13e5-aa4c-484d-97fe-de52eaff7177";
};
const getRxdaemonUserId = () => {
  return "test_rx_user_id";
};

const MockNexarApi: NexarApiContract = {
  ...NexarApi,
  getSubscriptionById,
  getSubscriptionsWithModules,
  sendOtpCode,
  validateOtpCodeAndLogin,
  activateSubscriptionById,
  isAuthenticated,
  createSubscriptionSetupIntent,
  deleteSubscriptionById,
  updateSubscriptionById,
  getUserData,
  getPersonalCloudUserId,
  getRxdaemonUserId,
};
export default MockNexarApi;
